import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Icon } from '../icon/icon'
import Text from '../text/text'
import { NavLink } from '~/components/internal-nav-link/Internal-nav-link'
import { useOptionalUser } from '~/utils/user'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '../ui/sheet'
import { ThemeSwitch } from '../theme-switch/theme-switch'
import LanguageSwitch from '../language-switch/language-switch'
import { getImgSrc } from '~/utils/image'

export default function MobileHeader() {
	const user = useOptionalUser()
	const { t } = useTranslation()
	const [isSheetOpen, setIsSheetOpen] = useState(false)

	const closeSheet = () => setIsSheetOpen(false) // Common styles for menu items
	const menuItemClasses =
		'flex w-full flex-row items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-accent hover:text-accent-foreground border-b border-border last:border-b-0'
	const lockedMenuItemClasses =
		'flex w-full flex-row items-center justify-between gap-3 px-4 py-3 text-sm transition-colors hover:bg-accent hover:text-accent-foreground border-b border-border last:border-b-0'

	return (
		<nav className="border-border bg-background text-foreground fixed bottom-0 z-20 block w-full rounded-t-lg border-t pt-1 shadow-xs md:hidden">
			<div className="flex items-center justify-between">
				<NavLink className="flex w-full flex-col py-2 text-center" to="/" end>
					{({ isActive }) => (
						<>
							<Icon
								className={isActive ? 'text-secondary' : ''}
								size="md"
								name="home"
							/>
							<Text as="sm">{t('misc_home')}</Text>
						</>
					)}
				</NavLink>
				{!user ? (
					<>
						<NavLink
							className="flex w-full flex-col py-2 text-center"
							to="/info"
						>
							{({ isActive }) => (
								<>
									<Icon
										className={isActive ? 'text-secondary' : ''}
										size="md"
										name="exclamation-circle"
									/>
									<Text as="sm">{t('misc_info')}</Text>
								</>
							)}
						</NavLink>
						<NavLink
							className="flex w-full flex-col py-2 text-center"
							to="/login"
						>
							{({ isActive }) => (
								<>
									<Icon
										className={isActive ? 'text-secondary' : ''}
										size="md"
										name="avatar"
									/>
									<Text as="sm">{t('action_login')}</Text>
								</>
							)}
						</NavLink>
					</>
				) : (
					<NavLink
						className="flex w-full flex-col py-2 text-center"
						to="/favorites"
					>
						{({ isActive }) => (
							<>
								<Icon
									className={isActive ? 'text-secondary' : ''}
									size="md"
									name="heart"
								/>
								<Text as="sm">{t('misc_favorites')}</Text>
							</>
						)}
					</NavLink>
				)}
				<Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
					<SheetTrigger asChild>
						<button className="flex w-full flex-col py-2 text-center">
							<Icon size="md" name="menu" />
							<Text as="sm">{t('misc_menu')}</Text>
						</button>
					</SheetTrigger>
					<SheetContent>
						<SheetHeader>
							<SheetTitle>
								{user && (
									<div className="flex items-center gap-3">
										<div className="bg-muted flex h-10 w-10 items-center justify-center rounded-full">
											{user.imageId ? (
												<img
													alt={user.name ?? ''}
													src={getImgSrc(user.imageId, 'user')}
													loading="lazy"
													className="h-10 w-10 rounded-full object-cover"
												/>
											) : (
												<Icon
													size="md"
													name="avatar"
													className="text-muted-foreground"
												/>
											)}
										</div>
										<div className="flex flex-col">
											<Text className="font-medium">{user.name}</Text>
											<Text as="sm" className="text-muted-foreground">
												{user.email}
											</Text>
										</div>
									</div>
								)}
							</SheetTitle>
						</SheetHeader>
						<div className="flex h-full flex-col">
							<div className="flex-1 p-2">
								{user ? (
									<>
										{/* All menu items for logged in users */}
										<NavLink
											className={menuItemClasses}
											to="/profile"
											onClick={closeSheet}
										>
											<Icon size="sm" name="avatar" />
											<Text>{t('misc_profile')}</Text>
										</NavLink>
										<NavLink
											className={menuItemClasses}
											to="/favorites"
											onClick={closeSheet}
										>
											<Icon size="sm" name="heart" />
											<Text>{t('misc_favorites')}</Text>
										</NavLink>
										<NavLink
											className={menuItemClasses}
											to="/my-events"
											onClick={closeSheet}
										>
											<Icon size="sm" name="calendar" />
											<Text>{t('profile_my_events')}</Text>
										</NavLink>
										<NavLink
											className={menuItemClasses}
											to="/my-events/new"
											onClick={closeSheet}
										>
											<Icon size="sm" name="plus" />
											<Text>{t('events_create_new')}</Text>
										</NavLink>
									</>
								) : (
									<>
										{/* All menu items for logged out users */}
										<NavLink
											className={menuItemClasses}
											to="/login"
											onClick={closeSheet}
										>
											<Icon size="sm" name="avatar" />
											<Text>{t('action_login')}</Text>
										</NavLink>
										<NavLink
											className={menuItemClasses}
											to="/signup"
											onClick={closeSheet}
										>
											<Icon size="sm" name="user-group" />
											<Text>{t('action_create_account')}</Text>
										</NavLink>
										<NavLink
											className={lockedMenuItemClasses}
											to="/favorites"
											onClick={closeSheet}
										>
											<div className="flex items-center gap-3">
												<Icon size="sm" name="heart" />
												<Text>{t('misc_favorites')}</Text>
											</div>
											<Icon
												size="xs"
												name="lock"
												className="text-muted-foreground"
											/>
										</NavLink>
										<NavLink
											className={lockedMenuItemClasses}
											to="/my-events/new"
											onClick={closeSheet}
										>
											<div className="flex items-center gap-3">
												<Icon size="sm" name="plus" />
												<Text>{t('events_create_new')}</Text>
											</div>
											<Icon
												size="xs"
												name="lock"
												className="text-muted-foreground"
											/>
										</NavLink>
										<NavLink
											className={menuItemClasses}
											to="/info"
											onClick={closeSheet}
										>
											<Icon size="sm" name="exclamation-circle" />
											<Text>{t('info_how_it_works')}</Text>
										</NavLink>
									</>
								)}
							</div>
							{/* Settings at bottom */}
							<div className="border-border bg-muted/20 space-y-3 border-t px-4 py-4">
								<Text
									as="sm"
									className="text-muted-foreground font-semibold tracking-wide uppercase"
								>
									{t('misc_settings')}
								</Text>
								<div className="space-y-2">
									<ThemeSwitch mobile />
									<LanguageSwitch mobile />
								</div>
								{user && (
									<div className="border-border/50 border-t pt-2">
										<NavLink
											className="text-muted-foreground hover:text-destructive hover:bg-destructive/5 flex items-center gap-2 rounded-md px-2 py-2 text-xs transition-colors"
											to="/logout"
											onClick={closeSheet}
										>
											<Icon size="xs" name="exit" />
											<Text as="xs">{t('action_logout')}</Text>
										</NavLink>
									</div>
								)}
							</div>
						</div>
					</SheetContent>
				</Sheet>
			</div>
		</nav>
	)
}
